import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ActivityFeedModelType,
    OrderStatusFilterOptions,
    QuoteStatusFilterOptions,
} from '../../../server/types/customer-activity-feed-types.ts';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { generateQueryParamsStringForActivity } from '../../activity-feed/hooks/use-activity-filters.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { PercentageDeltaBadge } from '../../insights-common/components/percentage-delta-badge.tsx';
import { ShowMoreArrow } from './show-more-arrow.tsx';

type CustomerFinancialsWithTimeframe =
    RouterOutput['platoUser']['getDetailCustomer']['getFinancialsWithTimeframe'];
type OpenTransactionSummary = RouterOutput['platoUser']['getDetailCustomer']['getOpenTransactionSummary'];

type ActivitiesStatsBarProps = {
    customerFinancialsWithTimeframe: CustomerFinancialsWithTimeframe;
    openTransactionSummary: OpenTransactionSummary | undefined;
    onScrollToTimeline: () => void;
};

function Dot({ className }: { className?: string }) {
    return <span className={`shrink-0 text-neutral-400 ${className ?? ''}`}>·</span>;
}

export function ActivitiesStatsBar({
    customerFinancialsWithTimeframe,
    openTransactionSummary,
    onScrollToTimeline,
}: ActivitiesStatsBarProps) {
    const { t } = useTranslation('customer-detail');
    const {
        canReadOpenOrders,
        canReadGrossMargin,
        canFilterQuoteOrderByStatus,
        canReadQuotes,
        defaultMetric,
        defaultTimeframe,
    } = useAppConfig();
    const sendEvent = useAnalytics();

    const openOrdersSearch = generateQueryParamsStringForActivity({
        modelType: ActivityFeedModelType.ORDER,
        orderStatuses: [OrderStatusFilterOptions.OPEN, OrderStatusFilterOptions.OPEN_IN_PICKING],
    });
    const openQuotesSearch = generateQueryParamsStringForActivity({
        modelType: ActivityFeedModelType.QUOTE,
        quoteStatuses: [QuoteStatusFilterOptions.OPEN],
    });

    return (
        <section
            aria-label={t('stats__activities_stats_label')}
            className="-mx-5 sm:-mx-4 flex shrink-0 items-center gap-3 overflow-x-auto whitespace-nowrap border-b border-neutral-200 px-5 text-sm md:border-b-0 scrollbar-none"
        >
            <div className="flex items-center gap-3">
                <span className="text-neutral-500">
                    {defaultTimeframe === 'TTM'
                        ? t('last_12_months', { ns: 'common' })
                        : t('stats__ytd_label', { year: new Date().getFullYear() })}
                </span>
                <div className="flex items-center gap-1">
                    <span className="font-semibold text-neutral-900">
                        <CurrencyOrEmpty>
                            {customerFinancialsWithTimeframe.total_amount_current}
                        </CurrencyOrEmpty>
                    </span>
                    <span className="text-neutral-500">
                        {defaultMetric === 'Order_Sum'
                            ? t('stats__decoration_order_sum')
                            : t('stats__decoration_revenue')}
                    </span>
                    <PercentageDeltaBadge>
                        {customerFinancialsWithTimeframe.total_amount_delta_percentage}
                    </PercentageDeltaBadge>
                </div>
                {canReadGrossMargin && (
                    <>
                        <Dot />
                        <div className="flex items-center gap-1">
                            <MaskedContent>
                                <span className="font-semibold text-neutral-900">
                                    <CurrencyOrEmpty>
                                        {customerFinancialsWithTimeframe.gross_margin_current}
                                    </CurrencyOrEmpty>
                                </span>
                                <span className="text-neutral-400">
                                    {' ('}
                                    <PercentageOrEmpty decimals={0}>
                                        {customerFinancialsWithTimeframe.gross_margin_percentage}
                                    </PercentageOrEmpty>
                                    {')'}
                                </span>
                                <span className="text-neutral-500">{t('stats__decoration_gm')}</span>
                                <PercentageDeltaBadge>
                                    {customerFinancialsWithTimeframe.gross_margin_delta_percentage}
                                </PercentageDeltaBadge>
                            </MaskedContent>
                        </div>
                    </>
                )}
            </div>
            <Dot />
            {canFilterQuoteOrderByStatus && (
                <div className="flex items-center gap-3">
                    {canReadOpenOrders && (
                        <Link
                            to={`?${openOrdersSearch}`}
                            replace
                            onClick={() => {
                                sendEvent('customer_detail_open_filter_clicked', {
                                    filter_type: 'open_orders',
                                });
                                onScrollToTimeline();
                            }}
                            className="group flex items-center gap-1"
                        >
                            <span className="font-semibold text-neutral-900">
                                {openTransactionSummary && (
                                    <NumberOrEmpty>{openTransactionSummary.openOrderCount}</NumberOrEmpty>
                                )}
                            </span>
                            <span className="text-neutral-500"> {t('stats__open_orders')}</span>
                            <ShowMoreArrow className="static" />
                        </Link>
                    )}
                    <Dot />
                    {canReadQuotes && (
                        <Link
                            to={`?${openQuotesSearch}`}
                            replace
                            onClick={() => {
                                sendEvent('customer_detail_open_filter_clicked', {
                                    filter_type: 'open_quotes',
                                });
                                onScrollToTimeline();
                            }}
                            className="group flex items-center gap-1"
                        >
                            <span className="font-semibold text-neutral-900">
                                {openTransactionSummary && (
                                    <NumberOrEmpty>{openTransactionSummary.openQuoteCount}</NumberOrEmpty>
                                )}
                            </span>
                            <span className="text-neutral-500">{t('stats__open_quotes')}</span>
                            <ShowMoreArrow className="static" />
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
}
